import React from "react";
import {
  AbsoluteFill,
  interpolate,
  OffthreadVideo,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// Paper Tape Banner with Realistic Scotch Tape
const PaperTape: React.FC<{
  text: string;
  subtext?: string;
  rotate?: number;
  scale?: number;
  bgColor?: string;
  textColor?: string;
  fontSize?: number;
}> = ({
  text,
  subtext,
  rotate = -1.5,
  scale = 1,
  bgColor = "#F5F0E6",
  textColor = "#18181B",
  fontSize = 32,
}) => {
  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: bgColor,
        padding: "16px 42px",
        borderRadius: 4,
        transform: `rotate(${rotate}deg) scale(${scale})`,
        boxShadow: "0 12px 30px rgba(0,0,0,0.5), 0 2px 4px rgba(0,0,0,0.2)",
        border: "1px solid rgba(0,0,0,0.08)",
      }}
    >
      {/* Scotch Tape Top-Left */}
      <div
        style={{
          position: "absolute",
          top: -14,
          left: -18,
          width: 56,
          height: 26,
          backgroundColor: "rgba(255, 250, 235, 0.7)",
          backdropFilter: "blur(2px)",
          border: "1px dashed rgba(200, 190, 170, 0.6)",
          transform: "rotate(-24deg)",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      />
      {/* Scotch Tape Top-Right */}
      <div
        style={{
          position: "absolute",
          top: -12,
          right: -18,
          width: 54,
          height: 26,
          backgroundColor: "rgba(255, 250, 235, 0.7)",
          backdropFilter: "blur(2px)",
          border: "1px dashed rgba(200, 190, 170, 0.6)",
          transform: "rotate(18deg)",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}
      />

      <span
        style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 900,
          fontSize,
          letterSpacing: -0.5,
          color: textColor,
          textTransform: "uppercase",
          lineHeight: 1.15,
          textAlign: "center",
        }}
      >
        {text}
      </span>
      {subtext && (
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize: fontSize * 0.44,
            letterSpacing: 1.6,
            color: "rgba(24, 24, 27, 0.75)",
            marginTop: 6,
            textTransform: "uppercase",
          }}
        >
          {subtext}
        </span>
      )}
    </div>
  );
};

// Floating Sticky Note
const StickyTag: React.FC<{
  label: string;
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  rotate?: number;
  scale?: number;
  color?: string;
}> = ({ label, top, bottom, left, right, rotate = 2, scale = 1, color = "#FDE047" }) => {
  return (
    <div
      style={{
        position: "absolute",
        top,
        bottom,
        left,
        right,
        transform: `rotate(${rotate}deg) scale(${scale})`,
        backgroundColor: color,
        color: "#18181B",
        padding: "8px 18px",
        borderRadius: 3,
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 800,
        fontSize: 15,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
        zIndex: 25,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -8,
          left: "50%",
          transform: "translateX(-50%) rotate(-3deg)",
          width: 32,
          height: 14,
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          border: "0.5px solid rgba(0,0,0,0.1)",
        }}
      />
      {label}
    </div>
  );
};

export const CreatorStudioShowcase: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 4 Narrative Beats across 235 frames (7.8 detik @ 30fps)
  // Phase 1: Frame 0 - 55   (Inside laptop screen preview)
  // Phase 2: Frame 55 - 120 (Dramatic Zoom-out to desk & chibi)
  // Phase 3: Frame 120 - 180(Reveal bento bot behind laptop)
  // Phase 4: Frame 180 - 235(Wide cinematic creator studio)
  const phase =
    frame < 55 ? 1 : frame < 120 ? 2 : frame < 180 ? 3 : 4;

  const phaseLocalFrame =
    frame < 55
      ? frame
      : frame < 120
      ? frame - 55
      : frame < 180
      ? frame - 120
      : frame - 180;

  const popSpring = spring({
    frame: phaseLocalFrame,
    fps,
    config: { damping: 13, mass: 0.65 },
  });

  const headerSpring = spring({
    frame,
    fps,
    config: { damping: 14, mass: 0.8 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#07090D",
        overflow: "hidden"
      }}
    >
      {/* 1. 3D CREATOR STUDIO CINEMATIC BACKGROUND WITH OFFICIAL ZURA AVATAR */}
      <AbsoluteFill>
        <OffthreadVideo
          src={staticFile("sumur_bor/zura_avatar_studio_intra.mp4")}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: "scale(1.02)",
          }}
        />
      </AbsoluteFill>

      {/* 2. SUBTLE VIGNETTES FOR TOP & BOTTOM TEXT SAFETY */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 380,
          background:
            "linear-gradient(180deg, rgba(7,9,13,0.85) 0%, rgba(7,9,13,0.3) 60%, transparent 100%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 520,
          background:
            "linear-gradient(0deg, rgba(7,9,13,0.92) 0%, rgba(7,9,13,0.4) 60%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      {/* 3. TOP PAPER TAPE (Safe Zone Y: 180px) */}
      <div
        style={{
          position: "absolute",
          top: 180,
          left: "50%",
          transform: `translateX(-50%) translateY(${interpolate(
            headerSpring,
            [0, 1],
            [-40, 0]
          )}px)`,
          zIndex: 30,
        }}
      >
        <PaperTape
          text="AVATAR 3D RESMI ZURA • AI LAB"
          subtext="HOODIE HITAM EMBLEM {Z} // ORISINAL BRAND IP"
          rotate={-1.5}
          scale={headerSpring}
          fontSize={33}
        />
      </div>

      {/* 4. DYNAMIC FLOATING STICKY TAGS (POP ACCORDING TO CAMERA DEPTH) */}
      {phase === 1 && (
        <StickyTag
          label="MULAI DARI DALAM SCREEN"
          top={480}
          right={130}
          rotate={-3}
          scale={popSpring}
          color="#FEF08A"
        />
      )}

      {phase === 2 && (
        <>
          <StickyTag
            label="ZOOM-OUT KE MEJA KERJA"
            top={440}
            left={120}
            rotate={3}
            scale={popSpring}
            color="#BAE6FD"
          />
          <StickyTag
            label="AVATAR RESMI ZURA {Z}"
            top={640}
            left={140}
            rotate={-2.5}
            scale={popSpring}
            color="#FED7AA"
          />
        </>
      )}

      {phase === 3 && (
        <>
          <StickyTag
            label="HOODIE STREETWEAR {Z}"
            top={450}
            right={140}
            rotate={-2}
            scale={popSpring}
            color="#BBF7D0"
          />
          <StickyTag
            label="CHIBI TECH CREATOR"
            top={680}
            right={120}
            rotate={3.5}
            scale={popSpring}
            color="#FDE047"
          />
        </>
      )}

      {phase === 4 && (
        <>
          <StickyTag
            label="100% IDENTITAS SENDIRI"
            top={440}
            left={140}
            rotate={-3}
            scale={popSpring}
            color="#F472B6"
          />
          <StickyTag
            label="BEBAS DI-ANIMASIKAN"
            top={660}
            right={130}
            rotate={2}
            scale={popSpring}
            color="#38BDF8"
          />
        </>
      )}

      {/* 5. BOTTOM PAPER TAPE SUBTITLES (Safe Zone Y: 1220 - 1500px) */}
      <div
        style={{
          position: "absolute",
          bottom: 390,
          left: "50%",
          transform: `translateX(-50%) translateY(${interpolate(
            popSpring,
            [0, 1],
            [30, 0]
          )}px)`,
          width: 900,
          display: "flex",
          justifyContent: "center",
          zIndex: 30,
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            backgroundColor: "#F7F4EB",
            padding: "26px 36px",
            borderRadius: 6,
            transform: `rotate(${phase % 2 === 0 ? 1.2 : -1.2}deg) scale(${popSpring})`,
            boxShadow: "0 18px 40px rgba(0,0,0,0.6), 0 4px 8px rgba(0,0,0,0.3)",
            border: "1.5px solid rgba(0,0,0,0.1)",
          }}
        >
          {/* Top Masking Tape Stripe */}
          <div
            style={{
              position: "absolute",
              top: -16,
              left: "50%",
              transform: "translateX(-50%) rotate(1deg)",
              width: 140,
              height: 32,
              backgroundColor: "rgba(255, 250, 235, 0.75)",
              border: "1px dashed rgba(180, 170, 150, 0.5)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            }}
          />

          {/* Phase Badge */}
          <div
            style={{
              display: "inline-block",
              backgroundColor: "#18181B",
              color: "#FEF08A",
              padding: "4px 14px",
              borderRadius: 3,
              fontFamily: "'Poppins', sans-serif",
              fontSize: 13,
              fontWeight: 900,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            {phase === 1 && "STEP 1: CLOSE-UP DALAM LAYAR"}
            {phase === 2 && "STEP 2: REVEAL AVATAR ZURA {Z}"}
            {phase === 3 && "STEP 3: STREETWEAR & LOGO ORISINAL"}
            {phase === 4 && "STEP 4: ASET 3D SIAP PAKAI"}
          </div>

          {/* Punchy Narrative Content */}
          <p
            style={{
              margin: 0,
              fontFamily: "'Poppins', sans-serif",
              fontSize: 24,
              fontWeight: 800,
              lineHeight: 1.35,
              color: "#18181B",
              textTransform: "uppercase",
              letterSpacing: -0.3,
            }}
          >
            {phase === 1 && (
              <>
                Kamera mulai dari dalam layar laptop, menyorot{" "}
                <span style={{ color: "#0284C7" }}>preview animasi & koding</span> secara close-up!
              </>
            )}
            {phase === 2 && (
              <>
                Lalu meluncur mundur menembus bezel, memperlihatkan{" "}
                <span style={{ color: "#EA580C" }}>Avatar 3D Resmi Zura</span> yang berdiri tegak!
              </>
            )}
            {phase === 3 && (
              <>
                Lengkap dengan kacamata cerdas, hoodie hitam sablon{" "}
                <span style={{ color: "#EA580C" }}>{`{Z}`}</span> oranye, dan sneakers modern!
              </>
            )}
            {phase === 4 && (
              <>
                Semua aset ini <span style={{ color: "#0284C7" }}>100% IDENTITAS ZURA</span>—bebas
                digerakkan & siap dipakai di setiap video TikTok!
              </>
            )}
          </p>
        </div>
      </div>
    </AbsoluteFill>
  );
};
